import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { softwareAppSchema, createBreadcrumbSchema } from '../lib/structured-data';
import { Check } from 'lucide-react';
import { track } from '../utils/analytics';
import { supabase } from '../lib/supabase';

const CALENDLY_URL = 'https://calendly.com/rcarloc029/30min';

export function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useEffect(() => { track('pricing_view'); }, []);

  const handleCheckout = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
      return;
    }

    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!res.ok) {
        const errBody = await res.text();
        console.error('Checkout failed:', res.status, errBody);
        throw new Error(`Checkout failed: ${errBody}`);
      }
      const { url } = await res.json() as { url?: string };
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Pricing"
          description="Situs Canada Professional plan: $49/month CAD with 7-day free trial. No credit card required. Canadian data sovereignty included. Cancel anytime."
          canonical="https://situsai.ca/pricing"
          ogImage="https://situsai.ca/logo.svg"
        />
        <JsonLd data={softwareAppSchema} />
        <JsonLd data={createBreadcrumbSchema('/pricing')} />

        {searchParams.get('trial_expired') === 'true' && (
          <div className="bg-amber-50 border-b border-amber-200 py-4 px-4 text-center">
            <p className="text-amber-800 font-medium">
              Your 7-day free trial has ended. Subscribe now to continue using Orca AI.
            </p>
          </div>
        )}
        {searchParams.get('welcome') === 'true' && (
          <div className="bg-emerald-50 border-b border-emerald-200 py-4 px-4 text-center">
            <p className="text-emerald-800 font-medium">
              Welcome! Your 7-day free trial is ready. Subscribe anytime for unlimited access.
            </p>
          </div>
        )}
        <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-[var(--color-csai-accent)] tracking-tight">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-2xl mx-auto mb-16">
            Canadian AI for professional practices. No hidden fees, no data harvesting. 7-day free trial, no credit card required.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto text-left">
            {/* Professional */}
            <Card className="p-8 border border-[var(--color-csai-surface)] flex flex-col bg-white hover:shadow-lg transition-shadow">
              <h3 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-2">Professional</h3>
              <p className="text-[var(--color-csai-muted)] text-sm mb-6">For solo practitioners and growing firms.</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-[var(--color-csai-accent)]">$49</span>
                <span className="text-[var(--color-csai-muted)]">/mo</span>
              </div>
              <p className="text-sm text-emerald-600 font-medium mb-6">7-Day Free Trial — No Credit Card</p>

              <div className="mb-8">
                <Button className="w-full h-12 text-base" onClick={handleCheckout}>Get Started</Button>
              </div>

              <ul className="space-y-4 flex-grow">
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span><strong>Orca AI Chat</strong> — full access</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>PDF upload and document analysis</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Canadian-focused AI assistance</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>100% Canadian data residency</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Zero data training policy</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Email support</span>
                </li>
              </ul>
            </Card>

            {/* Enterprise */}
            <Card className="p-8 border-2 border-[var(--color-csai-primary)] flex flex-col bg-white shadow-xl relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[var(--color-csai-primary)] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">For Firms</div>
              <h3 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-2">Enterprise</h3>
              <p className="text-[var(--color-csai-muted)] text-sm mb-6">For multi-person firms with custom needs.</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-[var(--color-csai-accent)]">Custom</span>
              </div>

              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="mb-8" onClick={() => track('calendly_click')}>
                <Button variant="secondary" className="w-full h-12 text-base">Chat With Us</Button>
              </a>

              <ul className="space-y-4 flex-grow">
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Everything in Professional</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Multi-user firm accounts</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Custom AI fine-tuning for your practice</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Dedicated account manager</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Priority 24/7 support</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--color-csai-muted)]">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Custom SLA</span>
                </li>
              </ul>
            </Card>
          </div>

          <p className="mt-12 text-sm text-[var(--color-csai-muted)]">
            Already have an account? <Link to="/login" className="text-[var(--color-csai-primary)] font-medium hover:underline">Sign in</Link>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
